import { Box, Button, Circle, Flex, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { LoaderOverlay } from 'components/LoaderOverlay/LoaderOverlay';
import { useNavigate, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { QrCode } from './QrCode/QrCode';
import { QrCodeSuccess } from './QrCode/QrCodeSuccess/QrCodeSuccess';
import { TicketInfo } from './TicketInfo/TicketInfo';
import { useTicketQuery } from './useTicket';
import { EventAnnouncements } from './TicketInfo/EventAnnouncements';
import { DownloadIcon } from 'lucide-react';
import { useEventTracking } from 'hooks/useEventTracking';
import { useEffect } from 'react';
import { usePopup } from 'context/PopupContext/PopupContext';
import { ROUTES } from 'router/routes';
import { InvitationTicketGenerator } from './InvitationTicketGenerator/InvitationTicketGenerator';

const TicketIcon = () => (
  <svg
    width='100'
    height='100'
    viewBox='0 0 100 100'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
  >
    <path
      d='M37.5 91.6667C37.5 88.3515 38.817 85.1721 41.1612 82.8279C43.5054 80.4837 46.6848 79.1667 50 79.1667C53.3152 79.1667 56.4947 80.4837 58.8389 82.8279C61.1831 85.1721 62.5 88.3515 62.5 91.6667H70.8334C73.0435 91.6667 75.1631 90.7888 76.7259 89.226C78.2887 87.6632 79.1667 85.5436 79.1667 83.3334V16.6667C79.1667 14.4566 78.2887 12.337 76.7259 10.7742C75.1631 9.21138 73.0435 8.33341 70.8334 8.33341H62.5C62.5 11.6486 61.1831 14.828 58.8389 17.1722C56.4947 19.5165 53.3152 20.8334 50 20.8334C46.6848 20.8334 43.5054 19.5165 41.1612 17.1722C38.817 14.828 37.5 11.6486 37.5 8.33341H29.1667C26.9566 8.33341 24.837 9.21138 23.2742 10.7742C21.7113 12.337 20.8334 14.4566 20.8334 16.6667L20.8334 83.3334C20.8334 85.5436 21.7113 87.6632 23.2742 89.226C24.837 90.7888 26.9566 91.6667 29.1667 91.6667H37.5Z'
      stroke='white'
      strokeWidth='5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M20.8334 45.8333H29.1667'
      stroke='white'
      strokeWidth='5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M70.8334 45.8333H79.1667'
      stroke='white'
      strokeWidth='5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M45.8334 45.8333H54.1667'
      stroke='white'
      strokeWidth='5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

export const TicketGeneratorPage = () => {
  const { eventTracking } = useEventTracking();
  const { showPopup } = usePopup();
  const navigate = useNavigate();
  useEffect(() => {
    eventTracking.page.viewed({
      pageName: 'ticket_generator_page',
      url: window.location.href,
    });
  }, [eventTracking]);
  const { ticketId } = useParams();
  const { data, isFetching, error } = useTicketQuery(ticketId);

  const backgroundGradient = useColorModeValue(
    'linear-gradient(135deg, #36d399 0%, #2bb384 100%)',
    'linear-gradient(135deg, #36d399 0%, #2bb384 100%)',
  );

  const isScanned = data?.used;
  useEffect(() => {
    if (isScanned) {
      eventTracking.eventDay.accessed({
        ticketId: data?.id,
        eventId: data?.event.id,
        ticketOrderId: data?.ticketOrderId,
      });
    }
  }, [data?.event.id, data?.id, data?.ticketOrderId, eventTracking, isScanned]);

  useEffect(() => {
    const status = error?.response?.status;
    if (status === 403 || status === 404) {
      const fallback =
        status === 404 ? 'التذكرة غير موجودة' : 'انتهى وقت المباراة، التذكرة غير صالحة';
      const apiMessage = error?.response?.data?.message || fallback;

      showPopup({
        message: apiMessage,
        type: 'ERROR',
      });
      navigate(ROUTES.home);
    }
  }, [error, navigate, showPopup]);
  if (isFetching) {
    return <LoaderOverlay />;
  }

  if (!data) return null;

  const handlePrint = () => {
    window.print();
  };

  if (data.invitationId) {
    return <InvitationTicketGenerator data={data} />;
  }

  return (
    <Flex
      flexDirection='column'
      justify='center'
      align='center'
      bgGradient={backgroundGradient}
      minH='100vh'
      w='100vw'
      p={{ base: '16px', md: '24px' }}
    >
      <Box py={'10px'} w='100%' maxW='520px'>
        <EventAnnouncements event_id={data.event.id} />
      </Box>
      <VStack spacing={{ base: '20px', md: '24px' }} w='100%' maxW='520px'>
        <Box
          bg='white'
          borderRadius='24px'
          boxShadow='0 10px 40px rgba(0, 0, 0, 0.15)'
          overflow='hidden'
          w='100%'
          transition='all 0.3s ease'
          _hover={{
            boxShadow: '0 15px 50px rgba(0, 0, 0, 0.2)',
          }}
        >
          <VStack width='100%' align='stretch' spacing={0}>
            <VStack
              borderTopRadius='20px'
              p={{ base: '24px', md: '32px' }}
              textAlign='center'
              minH='280px'
              justifyContent='center'
              position='relative'
              bg={data.category.color || 'linear-gradient(135deg, #326755 0%, #2a5647 100%)'}
              color='white'
              spacing='16px'
            >
              {!data.used && !data.qrCode && (
                <VStack spacing='20px' py='20px'>
                  <Box p='20px' display='flex' alignItems='center' justifyContent='center'>
                    <TicketIcon />
                  </Box>
                  <Text fontSize='2xl' fontWeight='bold' letterSpacing='wide'>
                    <FormattedMessage
                      id='ticketGenerator.eventTicket'
                      defaultMessage='تذكرة الحدث'
                    />
                  </Text>
                  {(data.concert || data.match) && (
                    <Box
                      textAlign='center'
                      bg='rgba(255, 255, 255, 0.1)'
                      p='16px'
                      borderRadius='12px'
                      maxW='90%'
                    >
                      <Text fontSize='md' fontWeight='semibold' mb='8px'>
                        <FormattedMessage
                          id='ticketGenerator.qrPlaceholder'
                          defaultMessage='سيظهر رمز التحقق هنا على هذه التذكرة قبل 6 ساعات من بدأ الحدث'
                        />
                      </Text>
                      <Text fontSize='sm' opacity='0.9'>
                        <FormattedMessage
                          id='ticketGenerator.qrPlaceholderScreenshot'
                          defaultMessage='التقط صورة للشاشة عند ظهور الرمز قبل الذهاب الى مكان الحدث'
                        />
                      </Text>
                    </Box>
                  )}
                </VStack>
              )}
              {!data.used && data.qrCode && (
                <Box
                  p='16px'
                  bg='white'
                  borderRadius='16px'
                  boxShadow='0 4px 12px rgba(0, 0, 0, 0.1)'
                >
                  <QrCode idToEncode={data.qrCode} />
                </Box>
              )}
              {data.used && <QrCodeSuccess />}
              <Circle
                size='40px'
                bg={backgroundGradient}
                position='absolute'
                top='50%'
                left='-20px'
                transform='translateY(-50%)'
                boxShadow='inset 0 2px 8px rgba(0, 0, 0, 0.1)'
              />
              <Circle
                size='40px'
                bg={backgroundGradient}
                position='absolute'
                top='50%'
                right='-20px'
                transform='translateY(-50%)'
                boxShadow='inset 0 2px 8px rgba(0, 0, 0, 0.1)'
              />
            </VStack>

            <TicketInfo
              item={data}
              gate={data.gate}
              holderName={data.holderName}
              holderPhone={data.holderPhone}
              sector={data.sector}
              isMatch={data.event.eventType.toString() === 'SPORT'}
              seatNumber={data.seatNumber}
              row={data.row}
              section={data.section}
              category={data.category}
              ticketType={data.ticketLabel || data.ticketType}
            />
          </VStack>
        </Box>

        {!data.used && data.qrCode && (
          <Button
            w='100%'
            maxW={{ base: '100%', md: '520px' }}
            h='56px'
            borderRadius='16px'
            bg='white'
            color='#005E51'
            fontSize='lg'
            fontWeight='bold'
            onClick={handlePrint}
            leftIcon={<DownloadIcon size={20} />}
            boxShadow='0 4px 12px rgba(0, 0, 0, 0.15)'
            transition='all 0.3s ease'
            _hover={{
              bg: '#F9F9F9',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            aria-label='Save ticket'
            tabIndex={0}
          >
            اضغط هنا لحفظ التذكرة
          </Button>
        )}
      </VStack>
    </Flex>
  );
};
